import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Info } from 'lucide-react';
import { money, dollars, pct0 } from './Ring';
import './forecast.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));
const fmtDate = (iso) => { const d = new Date(String(iso) + 'T12:00:00'); return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); };

/* Outer ring = likely finish vs target, inner ring = settled so far vs target. */
function DoubleRing({ likely, settled, target }) {
  const pOut = clamp(target ? likely / target : 0);
  const pIn = clamp(target ? settled / target : 0);
  const Co = 2 * Math.PI * 86, Ci = 2 * Math.PI * 64;
  const pct = target ? Math.round((likely / target) * 100) : 0;
  return (
    <div className="gpf-ring" data-testid="gpf-ring">
      <svg viewBox="-10 -10 220 220" role="img" aria-label={`Likely finish ${pct}% of target, settled ${Math.round(pIn * 100)}%`}>
        <circle className="orbit2" cx="100" cy="100" r="104" strokeWidth="1.2" />
        <circle className="orbit" cx="100" cy="100" r="104" strokeWidth="2" />
        <circle className="trk" cx="100" cy="100" r="86" strokeWidth="16" />
        <circle className="arc" cx="100" cy="100" r="86" strokeWidth="16" transform="rotate(-90 100 100)" stroke="var(--cyan)" strokeDasharray={`${(pOut * Co).toFixed(1)} ${Co.toFixed(1)}`} />
        <circle className="trk" cx="100" cy="100" r="64" strokeWidth="12" />
        <circle className="arc" cx="100" cy="100" r="64" strokeWidth="12" transform="rotate(-90 100 100)" stroke="var(--green)" strokeDasharray={`${(pIn * Ci).toFixed(1)} ${Ci.toFixed(1)}`} />
      </svg>
      <div className="gpf-ring-c"><b className="num" data-testid="gpf-ring-pct">{pct}%</b><span>likely, of target</span></div>
    </div>
  );
}

export function ForecastHero({ d }) {
  const r = d.range || {};
  const gapPositive = (d.gap || 0) > 0;
  return (
    <section className="gpj-panel" data-testid="gpf-hero-panel">
      <div className="gpf-hero">
        <div>
          <DoubleRing likely={d.likely} settled={d.settled} target={d.target} />
          <div className="gpf-legm"><span><i style={{ background: 'var(--cyan)' }} />Likely finish</span><span><i style={{ background: 'var(--green)' }} />Settled so far</span></div>
        </div>
        <div>
          <div className="gpf-eyebrow">{d.month_name} settlements forecast</div>
          <span className={`gpf-verdict v-${d.verdict?.tone || 'amber'}`} data-testid="gpf-verdict"><i /><span>{d.verdict?.label}</span></span>
          <div className="gpf-likely"><strong className="num" data-testid="gpf-likely">{dollars(d.likely)}</strong><span>likely finish against a {money(d.target, 0)} target</span></div>
          <div className="gpf-kv">
            <div><span>Settled so far</span><strong className="num green" data-testid="gpf-settled">{dollars(d.settled)}</strong></div>
            <div><span>80% range</span><strong className="num" data-testid="gpf-range">{money(r.low)} – {money(r.high)}</strong></div>
            <div><span>{gapPositive ? 'Gap to target' : 'Clear of target'}</span><strong className={`num ${gapPositive ? 'amber' : 'green'}`} data-testid="gpf-gap">{gapPositive ? money(d.gap) : money(Math.max(0, d.likely - d.target))}</strong></div>
            <div><span>Business days left</span><strong className="num" data-testid="gpf-bd-left">{d.business_days?.left ?? '–'}</strong></div>
          </div>
          <p className="gpf-andi" data-testid="gpf-andi"><span className="gpf-orb" aria-hidden="true" />{d.andi}</p>
        </div>
      </div>
    </section>
  );
}

export function PathToTarget({ d }) {
  const scale = Math.max(d.target || 0, d.likely || 0, 1);
  const r = d.range || {};
  const segs = (d.path || []).map((p) => ({ ...p, w: (p.value / scale) * 100 }));
  const gapVal = Math.max(0, (d.target || 0) - (d.likely || 0));
  const targetLeft = clamp((d.target || 0) / scale) * 100;
  const flip = targetLeft > 60;
  return (
    <section className="gpj-panel" data-testid="gpf-path-panel">
      <div className="gpj-panel-h"><h2>Path to {money(d.target, 0)}</h2><p>Each stage counts for its value times its chance of settling this month. Settled money counts in full.</p></div>
      <div className="gpf-path">
        <div className="gpf-bar" data-testid="gpf-path-bar">
          {segs.map((s) => <div key={s.key} className="gpf-seg" style={{ background: s.color, width: `${s.w}%` }} title={`${s.label}: ${money(s.value)}`} />)}
          {gapVal > 0 && <div className="gpf-seg gap" style={{ width: `${(gapVal / scale) * 100}%` }} title={`Gap: ${money(gapVal)}`} />}
          {(d.target || 0) > 0 && <div className={`gpf-tmark${flip ? ' flip' : ''}`} style={{ left: `${targetLeft}%` }}><span>Target {money(d.target, 0)}</span></div>}
        </div>
        {r.high > r.low && (
          <div className="gpf-rangebar" aria-hidden="true">
            <span className="lb" style={{ left: `${clamp(r.low / scale) * 100}%` }}>{money(r.low)}</span>
            <span className="ln" style={{ left: `${clamp(r.low / scale) * 100}%`, width: `${(clamp(r.high / scale) - clamp(r.low / scale)) * 100}%` }} />
            <span className="pt" style={{ left: `${clamp(d.likely / scale) * 100}%` }} />
            <span className="lb" style={{ left: `${clamp(r.high / scale) * 100}%`, transform: clamp(r.high / scale) > 0.8 ? 'translateX(-100%)' : undefined }}>{money(r.high)} · 80% range</span>
          </div>
        )}
      </div>
      <div className="gpf-tw">
        <table className="gpf-t" data-testid="gpf-stage-table">
          <thead><tr><th>Stage</th><th className="r">Files</th><th className="r">Pipeline</th><th>Chance to settle this month</th><th className="r">Counts as</th></tr></thead>
          <tbody>
            {(d.stage_table || []).map((s) => (
              <tr key={s.key}>
                <td><span className="gpf-sw" style={{ background: s.color }} />{s.label}</td>
                <td className="r num">{s.files}</td>
                <td className="r num">{money(s.pipeline)}</td>
                <td><div className="gpf-chance"><div className="tr"><i style={{ width: `${clamp(s.chance) * 100}%` }} /></div><b className="num">{pct0(s.chance)}</b></div></td>
                <td className="r num">{money(s.counts)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td>Likely finish</td><td /><td /><td style={{ color: 'var(--fg-3)' }}>{d.gap > 0 ? `Target ${money(d.target, 0)} · ${money(d.gap)} short` : 'At or above target'}</td><td className="r num">{money(d.likely)}</td></tr></tfoot>
        </table>
      </div>
    </section>
  );
}

export function DecideList({ d }) {
  const rows = d.decide || [];
  const nCover = d.cut_index != null ? d.cut_index + 1 : 0;
  return (
    <section className="gpj-panel" data-testid="gpf-decide-panel">
      <div className="gpj-panel-h"><h2>Files that decide the month</h2><p>Where a push this week moves the forecast most</p></div>
      {rows.length === 0 ? (
        <div className="gpf-callout green"><span>No open files need a push — the forecast holds on what's already in play.</span></div>
      ) : (
        <>
          {nCover > 0 && d.gap > 0 && <div className="gpf-callout amber"><span>The <b>top {nCover}</b> cover the <b>{money(d.gap)}</b> gap if they land this month.</span></div>}
          <div className="gpf-dl">
            {rows.map((row, i) => (
              <React.Fragment key={row.id || i}>
                <div className={`gpf-drow${d.cut_index != null && i <= d.cut_index ? ' in' : ''}`} data-testid={`gpf-decide-row-${i}`}>
                  <span className="n">{i + 1}</span>
                  <div><strong className="num">{dollars(row.value)}</strong><small>{row.client || `File ${row.id}`}{row.lender ? ` · ${row.lender}` : ''} · {row.stage_label?.toLowerCase()}</small></div>
                  <div className="act">{row.action}<small>Chance this month now {pct0(row.chance_now)}</small></div>
                  <div className="gain"><b className="num">+{money(row.lift)}</b><small style={{ display: 'block', color: 'var(--fg-3)' }}>forecast lift</small></div>
                </div>
                {row.cut && <div className="gpf-cutline">Gap closed</div>}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function NextMonth({ d }) {
  const nm = d.next_month || {};
  const scale = Math.max(nm.target || 0, nm.secured || 0, 1);
  const bookedW = (nm.booked / scale) * 100;
  const pipeW = (nm.pipeline / scale) * 100;
  const notCovW = clamp((nm.not_covered || 0) / scale) * 100;
  const pctSecured = nm.target ? Math.round((nm.secured / nm.target) * 100) : null;
  const paceRatio = nm.needed_per_bd ? clamp((nm.actual_per_bd || 0) / nm.needed_per_bd) : 1;
  return (
    <section className="gpj-panel" data-testid="gpf-nextmonth-panel">
      <div className="gpj-panel-h"><h2>{nm.name} outlook</h2><p>Is next month already in trouble?</p></div>
      <div className="gpf-eyebrow" style={{ fontSize: 11 }}>Secured from today's pipeline</div>
      <div className="gpf-nmbig"><strong className="num" data-testid="gpf-nm-secured">{dollars(nm.secured)}</strong><span>{nm.target ? `of ${money(nm.target, 0)} (${pctSecured}%)` : 'no target set'}</span></div>
      <div className="gpf-minibar">
        <div className="seg" style={{ background: 'var(--green)', width: `${bookedW}%` }} />
        <div className="seg" style={{ background: 'var(--cyan)', width: `${pipeW}%` }} />
        {(nm.not_covered || 0) > 0 && <div className="seg gap" style={{ width: `${notCovW}%` }} />}
      </div>
      <div className="gpf-minicap"><span>{money(nm.booked)} booked · {money(nm.pipeline)} likely from pipeline</span>{(nm.not_covered || 0) > 0 && <span>{money(nm.not_covered)} not yet covered</span>}</div>
      {(nm.not_covered || 0) > 0 ? (
        <div className="gpf-need" data-testid="gpf-need">
          <div className="gpf-eyebrow">To make {nm.name}</div>
          <div className="big">Lodge about <em>{money(nm.lodge_needed)}</em> more by {nm.cutoff_label}</div>
          <div className="gpf-pace">
            <div><span>Needed per business day</span><strong className="num">{money(nm.needed_per_bd)}</strong><div className="tr"><i style={{ width: '100%', background: 'var(--amber)' }} /></div></div>
            <div><span>Lodging per day so far this month</span><strong className="num">{money(nm.actual_per_bd)}</strong><div className="tr"><i style={{ width: `${paceRatio * 100}%`, background: 'var(--cyan)' }} /></div></div>
          </div>
          <p className="gpf-note">A file lodged after {nm.cutoff_label} is more likely to settle the month after. At an 80% lodgement-to-settlement rate, settling {money(nm.target, 0)} a month takes about {money((nm.target || 0) / 0.8, 0)} lodged.</p>
        </div>
      ) : (
        <div className="gpf-callout green" style={{ marginTop: 16 }}><span>{nm.name} is already covered by today's pipeline — keep lodging to build a buffer.</span></div>
      )}
    </section>
  );
}

export function Trend({ d, month }) {
  const pts = d.trend || [];
  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const W = 640, H = 270, pl = 52, pr = 70, pt = 18, pb = 30;
  const iw = W - pl - pr, ih = H - pt - pb;
  const maxY = Math.max(d.target || 0, (d.range?.high || 0), d.likely || 0, 10) * 1.15;
  const x = (day) => pl + (clamp((day - 1) / Math.max(1, daysInMonth - 1)) * iw);
  const yv = (v) => pt + ih - clamp(v / maxY) * ih;
  const likelyPts = pts.map((p) => `${x(p.day).toFixed(1)},${yv(p.likely).toFixed(1)}`).join(' ');
  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxY);
  const last = pts[pts.length - 1];
  return (
    <section className="gpj-panel gpf-trend" data-testid="gpf-trend-panel">
      <div className="gpj-panel-h"><h2>How the forecast has moved</h2><p>Likely finish each day it's been recorded</p></div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Forecast trend this month">
        {gridVals.map((v, i) => (<g key={i}><line x1={pl} x2={W - pr} y1={yv(v)} y2={yv(v)} style={{ stroke: 'rgba(var(--ink),.06)' }} /><text x={pl - 8} y={yv(v) + 4} textAnchor="end">{money(v, 0)}</text></g>))}
        {[1, Math.round(daysInMonth / 2), daysInMonth].map((dday, i) => <text key={i} x={x(dday)} y={H - 10} textAnchor="middle">{dday}</text>)}
        {(d.target || 0) > 0 && <line x1={pl} x2={W - pr} y1={yv(d.target)} y2={yv(d.target)} style={{ stroke: 'var(--fg)' }} strokeOpacity=".7" strokeDasharray="5 5" />}
        {(d.target || 0) > 0 && <text x={W - pr + 6} y={yv(d.target) + 4} className="lbl">{money(d.target, 0)}</text>}
        {pts.length > 1 && <polyline points={likelyPts} fill="none" stroke="var(--cyan)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
        {pts.map((p, i) => <circle key={i} cx={x(p.day)} cy={yv(p.likely)} r={i === pts.length - 1 ? 5 : 2.5} fill="var(--cyan)" style={i === pts.length - 1 ? { filter: 'drop-shadow(0 0 6px var(--cyan))' } : undefined} />)}
        {last && <text x={clamp(x(last.day) / W) > 0.7 ? x(last.day) - 8 : x(last.day) + 8} y={yv(last.likely) - 10} className="lbl" textAnchor={x(last.day) / W > 0.7 ? 'end' : 'start'}>Latest {money(last.likely)}</text>}
      </svg>
      {pts.length <= 1 && <p className="gpf-note">The trend line fills out as the forecast is recorded each day. Today is the first recorded point.</p>}
      <div className="gpf-legend"><span><i style={{ background: 'var(--cyan)' }} />Likely finish</span><span><i style={{ background: 'var(--fg)' }} />Target</span></div>
    </section>
  );
}

export function Outlook({ d }) {
  return (
    <section className="gpj-panel" data-testid="gpf-outlook-panel">
      <div className="gpj-panel-h"><h2>Three-month outlook</h2><p>From today's pipeline only. New lodgements add to later months.</p></div>
      <div className="gpf-outlook">
        {(d.outlook || []).map((o) => {
          const pctT = o.target ? Math.round((o.value / o.target) * 100) : null;
          const valW = o.target ? clamp(o.value / o.target) * 100 : (o.value > 0 ? 100 : 0);
          const setW = o.target ? clamp(o.settled / o.target) * 100 : 0;
          const tone = pctT == null ? 'var(--cyan)' : pctT >= 100 ? 'var(--green)' : pctT >= 85 ? 'var(--cyan)' : '#ff6961';
          return (
            <div className="gpf-orow" key={o.key} data-testid={`gpf-outlook-${o.key}`}>
              <b>{o.name}<small>{o.tag}</small></b>
              <div className="gpf-otrack">
                <div className="f" style={{ width: `${valW}%`, background: 'rgba(100,210,255,.5)' }} />
                {setW > 0 && <div className="f" style={{ width: `${setW}%`, background: 'var(--green)' }} />}
                {o.target > 0 && <span className="tm" style={{ left: '100%' }} />}
              </div>
              <div className="gpf-ov"><strong className="num" style={{ color: tone }}>{money(o.value)}</strong><small>{pctT != null ? `${pctT}% of ${money(o.target, 0)}` : 'no target'}</small></div>
            </div>
          );
        })}
      </div>
      <div className="gpf-legend"><span><i style={{ background: 'var(--green)' }} />Settled</span><span><i style={{ background: 'var(--cyan)' }} />Likely from pipeline</span><span><i style={{ background: 'var(--fg)' }} />Target</span></div>
    </section>
  );
}

export function Assumptions({ d, overrides, onChange }) {
  const a = d.assumptions || {};
  const val = (k, f) => (overrides[`${k}_${f}`] != null ? overrides[`${k}_${f}`] : a[k]?.[f === 'p' ? 'p' : f]);
  const rows = [
    ['booked', 'Booked to settle', '#30d158', false],
    ['approved', 'Approved, no date', '#64d2ff', true],
    ['conditional', 'Conditional', '#0a84ff', true],
    ['lodged', 'Lodged, awaiting', '#3d6fd9', true],
    ['ready', 'Ready to lodge', '#7a5cff', true],
  ];
  return (
    <details className="gpj-panel gpf-assume" data-testid="gpf-assumptions">
      <summary>How the forecast works and its assumptions</summary>
      <p className="gpf-note" style={{ margin: '12px 0' }}>Each file gets a chance of settling by a date, from its stage and how many business days a file at that stage usually takes. Adjust these to see how sensitive the result is. In the live build these rates calibrate from your own settled and withdrawn files.</p>
      <div className="gpf-tw">
        <table className="gpf-t">
          <thead><tr><th>Stage</th><th className="r">Chance it settles at all (%)</th><th className="r">Typical business days</th><th className="r">Give or take (days)</th></tr></thead>
          <tbody>
            {rows.map(([k, lbl, col, hasDays]) => (
              <tr key={k}>
                <td><span className="gpf-sw" style={{ background: col }} />{lbl}</td>
                <td className="r"><input type="number" min="0" max="100" value={val(k, 'p')} onChange={(e) => onChange(`${k}_p`, e.target.value)} data-testid={`gpf-asm-${k}-p`} /></td>
                <td className="r">{hasDays ? <input type="number" min="1" max="90" value={val(k, 'med')} onChange={(e) => onChange(`${k}_med`, e.target.value)} /> : <span style={{ color: 'var(--fg-3)' }}>On its date</span>}</td>
                <td className="r">{hasDays ? <input type="number" min="1" max="40" value={val(k, 'sd')} onChange={(e) => onChange(`${k}_sd`, e.target.value)} /> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export default function ForecastTab({ authToken, month, brokerFilter, sourceFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [overrides, setOverrides] = useState({});
  const timer = useRef(null);

  const fetchForecast = useCallback(async () => {
    if (!authToken || !month) return;
    try {
      const params = new URLSearchParams({ month });
      if (brokerFilter) params.append('broker', brokerFilter);
      if (sourceFilter) params.append('lead_source', sourceFilter);
      Object.entries(overrides).forEach(([k, v]) => { if (v !== '' && v != null) params.append(k, v); });
      const r = await axios.get(`${API_BASE}/api/gameplan/forecast?${params}`, { headers: { Authorization: `Bearer ${authToken}` } });
      setData(r.data); setError(false);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [authToken, month, brokerFilter, sourceFilter, overrides]);

  useEffect(() => { setLoading(true); fetchForecast(); }, [fetchForecast]);

  const onChange = (key, raw) => {
    const v = raw === '' ? '' : Number(raw);
    setOverrides((o) => ({ ...o, [key]: v }));
  };

  if (loading && !data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--fg-3)' }} /></div>;
  if (error && !data) return <div className="gpj-panel" data-testid="gpf-error"><div className="gpf-callout amber"><Info className="w-4 h-4" /><span>Couldn't load the forecast. Try refreshing.</span></div></div>;
  if (!data) return null;

  return (
    <div className="gpj-grid" data-testid="gpf-tab">
      <ForecastHero d={data} />
      <PathToTarget d={data} />
      <div className="gpj-grid gpj-two">
        <DecideList d={data} />
        <NextMonth d={data} />
      </div>
      <div className="gpj-grid gpj-two">
        <Trend d={data} month={month} />
        <Outlook d={data} />
      </div>
      <Assumptions d={data} overrides={overrides} onChange={onChange} />
    </div>
  );
}
